import React from 'react'; // FIX: Removed unused useEffect
import { Container, Typography, Box, CircularProgress, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useRTL } from '../../hooks/useRTL';
import { useAuth } from '../../context/AuthContext';
import NoPrivilege from '../../components/common/NoPrivilege';

// Components
import SummaryCards from './components/summeryCards.jsx';
import ChartsSection from './components/charts.jsx';
import TopRepresentatives from './components/topRepresnetative.jsx';
import RecentAppointments from './components/recentAppointments.jsx';
import BranchTotals from './components/branchTotals.jsx';

// Hooks
import { useDashboardData } from './hooks/useDashboard.js';
import { useDashboardFilters } from './hooks/useDashboardFilters';
import Appointments from '../appointments/Appointments.jsx';

const Dashboard = () => {
  const { t } = useTranslation();
  const { containerProps } = useRTL();
  const { user, isSuperAdmin } = useAuth();

  const canViewDashboard = isSuperAdmin || user?.privileges?.some(p =>
    p.module === 'dashboard' && p.operations.includes('view')
  );

  const {
    filters,
    setFilters,
    branches,
    allowedBranches,
    canSelectBranch
  } = useDashboardFilters(user, isSuperAdmin);

  // The hook now correctly re-fetches when 'filters' changes, thanks to its internal useEffect.
  const {
    analytics,
    loading,
    error,
    analyticsLoading,
    privilegeError,
    fetchAnalytics
  } = useDashboardData(user, isSuperAdmin, filters);

  // *** FIX: Removed the redundant useEffect that was causing extra fetches. ***
  // The useDashboardData hook now handles this internally by depending on the `filters` object.

  if (!canViewDashboard || privilegeError) {
    return <NoPrivilege message={t('dashboard.noPrivilegeError')} />;
  }

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!analytics && !analyticsLoading) {
    return (
      <Container maxWidth="xl">
        <Alert severity="info" sx={{ mt: 2 }}>
          {t('common.noData')}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 3, ...containerProps }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 4 }}>
        {t('navigation.dashboard')}
      </Typography>

      {user.userType !== 'representative' && (
        <SummaryCards
          analytics={analytics}
          analyticsLoading={analyticsLoading}
          error={error}
          branches={branches}
          filters={filters}
          setFilters={setFilters}
          allowedBranches={allowedBranches}
          canSelectBranch={canSelectBranch}
        />)}

      <ChartsSection
        analytics={analytics}
        analyticsLoading={analyticsLoading}
        error={error}
      />

      <TopRepresentatives
        analytics={analytics}
        analyticsLoading={analyticsLoading}
        error={error}
      />

      <RecentAppointments
        analytics={analytics}
        analyticsLoading={analyticsLoading}
        error={error}
        // Pass the latest filters to the manual refresh button
        onRefresh={() => fetchAnalytics(filters)}
      />
      <Appointments
        daily={false}
      />

      <BranchTotals
        analytics={analytics}
        analyticsLoading={analyticsLoading}
        user={user}
        isSuperAdmin={isSuperAdmin}
        filters={filters}
      />
    </Container>
  );
};

export default Dashboard;